import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';

// Internal use only
enum PageType {
  page = 'page',
  next = 'next',
  previous = 'previous',
  current = 'current',
  ellipsis = 'ellipsis',
}

// Internal use only
interface Page {
  page: number;
  label?: string;
  active?: boolean;
  pageType: PageType;
}

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.css',
  standalone: true,
  imports: [CommonModule],
})
export class PaginatorComponent {
  private _length!: number;

  @Input()
  set length(value: number) {
    this._length = value;
    this.page.set(1);
    const pages = this.createPages();
    this.pages.set(pages);
  }
  @Input() set reset(_: number) {
    this._setLength(this._length);
  }

  @Input()
  pageSize = 16;
  @Input()
  maxPages = 4;

  @Output()
  pageChanged = new EventEmitter<number>();

  page = signal(1);
  pages = signal<Page[]>([]);

  onPageClick(event: Event, page: Page): void {
    event.preventDefault();
    this.page.set(page.page);
    const pages = this.createPages();
    this.pages.set(pages);
    this.pageChanged.emit(page.page);
  }

  private createPages(): Page[] {
    const currentPages = [];
    const currentLength = this._length;

    if (!currentLength) {
      return currentPages;
    }

    const currentPage = this.page();
    const currentPageSize = this.pageSize;
    const currentMaxPages = this.maxPages;
    const totalPages = Math.ceil(currentLength / currentPageSize);

    if (totalPages < 2) {
      return currentPages;
    }

    const max = totalPages > currentMaxPages ? currentMaxPages : totalPages;
    const pageGroup = Math.ceil(currentPage / currentMaxPages);
    const endPage = pageGroup * max;
    const startPage = endPage - max + 1;

    if (currentPage > 1) {
      currentPages.push({
        page: currentPage - 1,
        pageType: PageType.previous,
      });
    }

    if (currentPage > currentMaxPages) {
      currentPages.push({
        page: startPage - 1,
        label: '...',
        pageType: PageType.ellipsis,
      });
    }

    for (let page = startPage; page <= endPage; page++) {
      if (page <= totalPages) {
        currentPages.push({
          page,
          active: page === currentPage,
          label: `${page}`,
          pageType: PageType.page,
        });
      }
    }

    if (
      currentPage < totalPages &&
      endPage - startPage + 1 === currentMaxPages &&
      endPage !== totalPages
    ) {
      currentPages.push({
        page: endPage + 1,
        label: '...',
        pageType: PageType.ellipsis,
      });
    }

    if (currentPage < totalPages) {
      currentPages.push({
        page: currentPage + 1,
        pageType: PageType.next,
      });
    }

    return currentPages;
  }

  private _setLength(value: number) {
    this._length = value;
    this.page.set(1);
    this.pages.set(this.createPages());
  }
}
